import type {LabelPickerLabel} from '@github-ui/item-picker-label'
import {DOCUMENT_FIELDS_PATH, type SetFieldsOperation} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useMutation} from '@github-ui/react-query'
import {updateLabelsForLabelableRelayMutation as updateLabelsForLabelableMutationQueryId} from '@github-ui/server-persisted-queries'
import type {UpdateLabelsForLabelableRelayMutationMutation} from '@github-ui/server-persisted-queries/types/updateLabelsForLabelableRelayMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {commitLocalUpdate, type Disposable, type Environment, type RecordSourceProxy} from 'relay-runtime'

type LabelForProjection = Pick<
  LabelPickerLabel,
  'id' | 'name' | 'nameHTML' | 'color' | 'description' | 'url' | 'archivedAt'
>

const LABELS_CONNECTION_ARGS = {first: 100, orderBy: {field: 'NAME', direction: 'ASC'}} as const
const fallbackErrorMessage = 'Failed to update issue labels'
let updateLabelsOperationSequence = 0

type UpdateLabelsMutationInput = {
  labelableId: string
  labelsToAdd: LabelPickerLabel[]
  labelsToRemove: LabelPickerLabel[]
  labelableTypeName: 'Issue' | 'PullRequest'
}

type UpdateLabelsMutationCallbacks = {
  clientMutationId?: string
  onError?: (error: Error) => void
  onCompleted?: (response: UpdateLabelsForLabelableRelayMutationMutation) => void
  /** Expected final labels after the mutation, used for optimistic response */
  expectedFinalLabels: LabelPickerLabel[]
}

type UpdateLabelsPendingRequest = UpdateLabelsMutationInput &
  Pick<UpdateLabelsMutationCallbacks, 'clientMutationId' | 'expectedFinalLabels'>

type UpdateLabelsEntityMutationVariables = {
  operation: SetFieldsOperation
  operationId: string
  request: UpdateLabelsPendingRequest
}

function projectLabelFields(labels: readonly LabelPickerLabel[]) {
  return {
    pageInfo: {hasNextPage: false, endCursor: null},
    edges: labels.map(label => ({
      node: {
        id: label.id,
        url: label.url,
        name: label.name,
        nameHTML: label.nameHTML,
        color: label.color,
        description: label.description ?? null,
        archivedAt: label.archivedAt ?? null,
      },
    })),
  }
}

function writeRelayLabels(
  store: RecordSourceProxy,
  labelableId: string,
  labels: readonly LabelForProjection[],
  recordKey: string,
) {
  const labelable = store.get(labelableId)
  if (!labelable) return

  const connection = labelable.getOrCreateLinkedRecord('labels', 'LabelConnection', LABELS_CONNECTION_ARGS)
  const pageInfo = connection.getOrCreateLinkedRecord('pageInfo', 'PageInfo')
  pageInfo.setValue(false, 'hasNextPage')
  pageInfo.setValue(null, 'endCursor')

  const edges = labels.map((label, index) => {
    const edge = store.create(`client:${recordKey}:labels:edge:${index}:${label.id}`, 'LabelEdge')
    edge.setValue(label.id, 'cursor')

    const node = store.get(label.id) ?? store.create(label.id, 'Label')
    node.setValue(label.id, 'id')
    node.setValue(label.name, 'name')
    node.setValue(label.nameHTML, 'nameHTML')
    node.setValue(label.color, 'color')
    node.setValue(label.description ?? null, 'description')
    node.setValue(label.url, 'url')
    node.setValue(label.archivedAt ?? null, 'archivedAt')

    edge.setLinkedRecord(node, 'node')
    return edge
  })

  connection.setLinkedRecords(edges, 'edges')
}

async function updateLabelsForLabelableRequest({
  labelableId,
  labelsToAdd,
  labelsToRemove,
  clientMutationId,
  signal,
}: {
  labelableId: string
  labelsToAdd: LabelPickerLabel[]
  labelsToRemove: LabelPickerLabel[]
  clientMutationId?: string
  signal: AbortSignal
}): Promise<UpdateLabelsForLabelableRelayMutationMutation> {
  const query = new URLSearchParams({queryId: updateLabelsForLabelableMutationQueryId})
  const response = await reactFetchJSON(`/label_assignments.json?${query.toString()}`, {
    method: 'PATCH',
    body: {
      labelableId,
      addLabelIds: labelsToAdd.map(label => label.id),
      removeLabelIds: labelsToRemove.map(label => label.id),
      ...(clientMutationId ? {clientMutationId} : {}),
    },
    signal,
  })

  if (!response.ok) {
    throw new Error(`${fallbackErrorMessage} (${response.status})`)
  }

  const result = (await response.json()) as {
    data?: UpdateLabelsForLabelableRelayMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  if (!result.data) {
    throw new Error(result.errors?.[0]?.message ?? fallbackErrorMessage)
  }

  return result.data
}

function completeLabelsOrThrow(data: UpdateLabelsForLabelableRelayMutationMutation): LabelForProjection[] {
  const labelable = data.updateLabelsForLabelable?.labelable
  const connection = labelable?.labels
  const edges = connection?.edges
  const completeLabels =
    connection && !connection.pageInfo.hasNextPage && edges
      ? edges.flatMap(edge => (edge?.node ? [edge.node] : []))
      : undefined
  if (!completeLabels || completeLabels.length !== edges?.length) throw new Error(fallbackErrorMessage)
  return completeLabels
}

async function notifyMutationResult(
  mutation: Promise<UpdateLabelsForLabelableRelayMutationMutation>,
  callbacks?: Pick<UpdateLabelsMutationCallbacks, 'onError' | 'onCompleted'>,
): Promise<void> {
  let response: UpdateLabelsForLabelableRelayMutationMutation
  try {
    response = await mutation
  } catch (error) {
    callbacks?.onError?.(error instanceof Error ? error : new Error(fallbackErrorMessage))
    return
  }
  callbacks?.onCompleted?.(response)
}

/**
 * Computes the diff between initial and selected labels
 */
export function computeLabelDiff(
  initialLabels: LabelPickerLabel[],
  selectedLabels: LabelPickerLabel[],
): {labelsToAdd: LabelPickerLabel[]; labelsToRemove: LabelPickerLabel[]} {
  const initialIds = new Set(initialLabels.map(l => l.id))
  const selectedIds = new Set(selectedLabels.map(l => l.id))

  const labelsToAdd = selectedLabels.filter(label => !initialIds.has(label.id))
  const labelsToRemove = initialLabels.filter(label => !selectedIds.has(label.id))

  return {labelsToAdd, labelsToRemove}
}

export type UpdateLabelsForLabelable = (
  parameters: {input: UpdateLabelsMutationInput} & UpdateLabelsMutationCallbacks,
) => void

export function useUpdateLabelsForLabelableMutation({
  environment,
  clientMutationId,
}: {
  environment: Environment
  clientMutationId?: string
}): UpdateLabelsForLabelable {
  const {mutateAsync} = useMutation<
    UpdateLabelsForLabelableRelayMutationMutation,
    Error,
    UpdateLabelsEntityMutationVariables,
    Disposable
  >({
    mutationKey: entityFieldsMutationKey(),
    scope: {id: 'update-labels-for-labelable'},
    mutationFn: ({request}) =>
      updateLabelsForLabelableRequest({
        labelableId: request.labelableId,
        labelsToAdd: request.labelsToAdd,
        labelsToRemove: request.labelsToRemove,
        clientMutationId: request.clientMutationId,
        signal: new AbortController().signal,
      }),
    onMutate: ({operationId, request}) =>
      environment.applyUpdate({
        storeUpdater: store =>
          writeRelayLabels(store, request.labelableId, request.expectedFinalLabels, `${operationId}:optimistic`),
      }),
    onSuccess: async (data, variables, relayUpdate) => {
      const {request, operationId} = variables
      const completeLabels = completeLabelsOrThrow(data)
      commitLocalUpdate(environment, store =>
        writeRelayLabels(store, request.labelableId, completeLabels, `${operationId}:confirmed`),
      )
      relayUpdate.dispose()
      if (request.labelableTypeName === 'Issue') {
        await notifySettlement(
          {type: 'setFields', id: request.labelableId, fields: {labels: projectLabelFields(completeLabels)}},
          ENTITY_FIELDS_OWNER,
          DOCUMENT_FIELDS_PATH,
          operationId,
        )
      }
    },
    onError: (_error, _variables, relayUpdate) => {
      relayUpdate?.dispose()
    },
  })

  return useCallback<UpdateLabelsForLabelable>(
    ({input, expectedFinalLabels, onError, onCompleted, clientMutationId: perCallClientMutationId}) => {
      const operation: SetFieldsOperation = {
        type: 'setFields',
        id: input.labelableId,
        fields: {labels: projectLabelFields(expectedFinalLabels)},
      }
      const operationId = `update-labels-${updateLabelsOperationSequence++}`
      void notifyMutationResult(
        mutateAsync({
          operation,
          operationId,
          request: {
            ...input,
            expectedFinalLabels,
            clientMutationId: perCallClientMutationId ?? clientMutationId,
          },
        }),
        {onError, onCompleted},
      )
    },
    [clientMutationId, mutateAsync],
  )
}
